import { Button } from '../Button'
import { EmptyState } from '../EmptyState'
import { ApiError } from '@/lib/api/jsonFetch'

export interface QueryErrorStateProps {
  /** The rejected value from a React Query `error` field. Any shape. */
  error: unknown
  /** Wired to the query's `refetch`. Omit to hide the Retry button. */
  onRetry?: () => void
  /** What failed to load, lowercase — "open orders", "quotes". Used in
   *  the generic copy: "Couldn't load open orders". */
  subject?: string
  className?: string
}

interface Copy {
  title: string
  description: string
}

function isOdooUnavailable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  const d = error.detail
  return typeof d === 'object' && d !== null && (d as { code?: string }).code === 'odoo_unavailable'
}

/**
 * Turns a thrown `ApiError` into copy that tells the operator which of
 * the three very different failures they hit. Anything we can't classify
 * gets the generic transport-failure wording — never "no results", which
 * is the whole point of this component.
 */
function copyFor(error: unknown, subject: string): Copy {
  const status = error instanceof ApiError ? error.status : undefined

  if (status === 401) {
    return {
      title: 'Session expired',
      description: 'Sign in again to reload this page.',
    }
  }
  if (status === 403) {
    return {
      title: 'Access denied',
      description: `Your role doesn't have permission to view ${subject}. Ask an admin to check your access.`,
    }
  }
  if (status === 404) {
    return {
      title: 'Not found',
      description: `That ${subject} record no longer exists, or the link is out of date.`,
    }
  }
  if (status === 503 && isOdooUnavailable(error)) {
    return {
      title: 'Odoo is unreachable',
      description: `${subject[0].toUpperCase()}${subject.slice(1)} can't load until the connection to Odoo returns. Nothing is lost — retry in a minute.`,
    }
  }
  return {
    title: `Couldn't load ${subject}`,
    description:
      status && status >= 500
        ? 'The server hit an error. This is usually temporary — retry in a moment.'
        : 'The request failed before it finished. Check your connection and retry.',
  }
}

/**
 * The failure counterpart to `<EmptyState variant="no-match">`.
 *
 * Ops pages used to render `data?.rows ?? []` on a rejected query, so a
 * 403, a 502 and an aborted fetch all came out as an empty table reading
 * "No matches — try clearing a filter". That is indistinguishable from a
 * successful query with no results, which cost a full debugging session
 * on 2026-09-10: Open Orders looked like a sales-rep permission bug for
 * hours while the backend was authorizing correctly and returning 212
 * rows. Rendering *something failed* is the fix — pair every list query
 * with this, never with the empty state.
 */
export function QueryErrorState({
  error,
  onRetry,
  subject = 'this data',
  className,
}: QueryErrorStateProps) {
  const { title, description } = copyFor(error, subject)
  const detail = error instanceof Error ? error.message : null

  return (
    <EmptyState
      variant="error"
      title={title}
      description={
        <>
          {description}
          {detail ? (
            <span className="mt-2 block break-words text-xs text-tertiary">{detail}</span>
          ) : null}
        </>
      }
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
      className={className}
    />
  )
}
